/**
 * @description
 * User analytics panel component with statistics and visualizations.
 * Displays user distribution charts, statistics cards, and top users table.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/components/vscode-lens/UserAnalytics.test.tsx
 */

import { useState, useMemo } from 'react';
import { BarChart3, ChevronDown, ChevronUp, PieChart, TrendingUp, Users } from 'lucide-react';
import { BarChart, Bar, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { UserAggregation, UserStatistics, UserFilterConfig, VSCodeLensTable, StreamRecord } from '../../../types/vscode-lens';
import { getUserColor, formatUserEmail } from '../../utils/domainUtils';

interface UserAnalyticsProps {
  aggregation: UserAggregation;
  statistics: UserStatistics | null;
  filterConfig: UserFilterConfig;
  onFilterChange: (config: UserFilterConfig) => void;
  table: VSCodeLensTable;
  rawData: StreamRecord[];
}

/**
 * User analytics panel component
 */
export function UserAnalytics({
  aggregation,
  statistics,
  filterConfig,
  onFilterChange,
  table,
  rawData,
}: UserAnalyticsProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [chartType, setChartType] = useState<'bar' | 'pie'>('bar');

  // Generate table-specific chart data
  const tableSpecificChartData = useMemo(() => {
    if (!rawData.length) return null;

    switch (table) {
      case 'commit_events':
        return generateCommitChartData(rawData, aggregation?.topUsers || []);
      case 'conversation_messages':
        return generateMessageChartData(rawData, aggregation?.topUsers || []);
      case 'llm_completions':
        return generateCompletionChartData(rawData, aggregation?.topUsers || []);
      default:
        return null;
    }
  }, [table, rawData, aggregation?.topUsers]);

  if (!aggregation || aggregation.users.length === 0) {
    return null;
  }

  const { users, topUsers } = aggregation;

  // Prepare chart data (top 10 users)
  const chartData = topUsers.map((u, index) => ({
    user: u.user.length > 20 ? u.user.substring(0, 17) + '...' : u.user,
    fullUser: u.user,
    count: u.count,
    percentage: u.percentage,
    color: getUserColor(u.user, index),
  }));

  const handleUserClick = (user: string) => {
    // Toggle user selection
    const newSelected = new Set(filterConfig.selectedUsers);
    if (newSelected.has(user)) {
      newSelected.delete(user);
    } else {
      newSelected.add(user);
    }
    
    onFilterChange({
      ...filterConfig,
      mode: 'include',
      enabled: true,
      selectedUsers: newSelected,
    });
  };

  return (
    <div className="rounded-lg border bg-card">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">User Analytics</h2>
          <span className="text-sm text-muted-foreground">
            ({users.length} unique users)
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5" />
        ) : (
          <ChevronDown className="h-5 w-5" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4 pt-0 space-y-6">
          {/* Statistics Cards */}
          {statistics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                label="Total Users"
                value={statistics.totalUsers.toString()}
                color="blue"
              />
              <StatCard
                icon={BarChart3}
                label="Avg Records/User"
                value={Math.round(statistics.averageRecordsPerUser).toString()}
                color="green"
              />
              <StatCard
                icon={TrendingUp}
                label="Most Active User"
                value={statistics.mostActiveUser?.user || 'N/A'}
                subtitle={statistics.mostActiveUser ? `${statistics.mostActiveUser.count} records` : undefined}
                color="purple"
              />
              <StatCard
                icon={PieChart}
                label="Concentration"
                value={`${statistics.userConcentration.toFixed(1)}%`}
                subtitle="in top user"
                color="orange"
              />
            </div>
          )}

          {/* Table-Specific Chart */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">
              {table === 'commit_events' && 'Commit Activity by User'}
              {table === 'conversation_messages' && 'Message Activity by User'}
              {table === 'llm_completions' && 'Completion Activity by User'}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => setChartType('bar')}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                  chartType === 'bar'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-input hover:bg-accent'
                }`}
              >
                <BarChart3 className="h-3 w-3 inline mr-1" />
                Bar Chart
              </button>
              <button
                onClick={() => setChartType('pie')}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                  chartType === 'pie'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-input hover:bg-accent'
                }`}
              >
                <PieChart className="h-3 w-3 inline mr-1" />
                Pie Chart
              </button>
            </div>
          </div>

          {/* Table-Specific Charts */}
          <div className="h-80">
            {tableSpecificChartData ? (
              <ResponsiveContainer width="100%" height="100%">
                {tableSpecificChartData.chartType === 'bar' ? (
                  <BarChart data={tableSpecificChartData.data}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="label" 
                      className="text-xs"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis className="text-xs" />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="rounded-lg border bg-card p-3 shadow-lg">
                              <p className="font-medium">{data.fullLabel}</p>
                              <p className="text-sm text-muted-foreground">
                                {data.value} {tableSpecificChartData.unit}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar
                      dataKey="value"
                      fill="#3b82f6"
                      radius={[4, 4, 0, 0]}
                      onClick={(data) => {
                        if (data && typeof data === 'object' && 'user' in data) {
                          handleUserClick(data.user as string);
                        }
                      }}
                      className="cursor-pointer"
                    >
                      {tableSpecificChartData.data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                ) : (
                  <RechartsPie>
                    <Pie
                      data={tableSpecificChartData.data}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(props) => {
                        const payload = props.payload as { label?: string; percentage?: number } | undefined;
                        const label = payload?.label || '';
                        const percentage = payload?.percentage || 0;
                        return `${label} (${percentage.toFixed(1)}%)`;
                      }}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      onClick={(data) => {
                        if (data && typeof data === 'object' && 'user' in data) {
                          handleUserClick(data.user as string);
                        }
                      }}
                      className="cursor-pointer"
                    >
                      {tableSpecificChartData.data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="rounded-lg border bg-card p-3 shadow-lg">
                              <p className="font-medium">{data.fullLabel}</p>
                              <p className="text-sm text-muted-foreground">
                                {data.value} {tableSpecificChartData.unit}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </RechartsPie>
                )}
              </ResponsiveContainer>
            ) : (
              // Fallback to user distribution chart
              chartType === 'bar' ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="user" 
                      className="text-xs"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis className="text-xs" />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="rounded-lg border bg-card p-3 shadow-lg">
                              <p className="font-medium">{data.fullUser}</p>
                              <p className="text-sm text-muted-foreground">
                                {data.count} records ({data.percentage.toFixed(1)}%)
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar
                      dataKey="count"
                      fill="#3b82f6"
                      radius={[4, 4, 0, 0]}
                      onClick={(data) => {
                        if (data && typeof data === 'object' && 'fullUser' in data) {
                          handleUserClick(data.fullUser as string);
                        }
                      }}
                      className="cursor-pointer"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(props) => {
                        const payload = props.payload as { user?: string; percentage?: number } | undefined;
                        const user = payload?.user || '';
                        const percentage = payload?.percentage || 0;
                        return `${user} (${percentage.toFixed(1)}%)`;
                      }}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                      onClick={(data) => {
                        if (data && typeof data === 'object' && 'fullUser' in data) {
                          handleUserClick(data.fullUser as string);
                        }
                      }}
                      className="cursor-pointer"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="rounded-lg border bg-card p-3 shadow-lg">
                              <p className="font-medium">{data.fullUser}</p>
                              <p className="text-sm text-muted-foreground">
                                {data.count} records ({data.percentage.toFixed(1)}%)
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              )
            )}
          </div>

          {/* Top Users Table */}
          <div>
            <h3 className="text-sm font-medium mb-3">Top 10 Users</h3>
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">#</th>
                    <th className="px-4 py-2 text-left font-medium">User</th>
                    <th className="px-4 py-2 text-left font-medium">Domain</th>
                    <th className="px-4 py-2 text-right font-medium">Records</th>
                    <th className="px-4 py-2 text-right font-medium">Percentage</th>
                    <th className="px-4 py-2 text-center font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {topUsers.map((user, index) => {
                    const isSelected = filterConfig.selectedUsers.has(user.user);
                    return (
                      <tr
                        key={user.user}
                        className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3 text-muted-foreground">{index + 1}</td>
                        <td className="px-4 py-3 font-medium" title={user.user}>
                          {formatUserEmail(user.user, 40)}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{user.domain}</td>
                        <td className="px-4 py-3 text-right">{user.count.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">{user.percentage.toFixed(2)}%</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleUserClick(user.user)}
                            className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                              isSelected
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background border-input hover:bg-accent'
                            }`}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Help Text */}
          <p className="text-xs text-muted-foreground">
            Click on chart bars or use the Select buttons to filter data by user.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Generate commit-specific chart data
 */
function generateCommitChartData(data: StreamRecord[], topUsers: { user: string; count: number; percentage: number }[]) {
  const userCommitData = new Map<string, { commits: number; linesAdded: number; linesDeleted: number; filesChanged: number }>();
  
  data.forEach(record => {
    const user = record.user_email || record.author_email || 'unknown';
    const existing = userCommitData.get(user) || { commits: 0, linesAdded: 0, linesDeleted: 0, filesChanged: 0 };
    
    existing.commits++;
    existing.linesAdded += record.lines_added || 0;
    existing.linesDeleted += record.lines_deleted || 0;
    existing.filesChanged += record.files_changed || record.files_modified || 0;
    
    userCommitData.set(user, existing);
  });

  const chartData = topUsers.map((user, index) => {
    const commitData = userCommitData.get(user.user) || { commits: 0, linesAdded: 0, linesDeleted: 0, filesChanged: 0 };
    return {
      label: user.user.length > 15 ? user.user.substring(0, 12) + '...' : user.user,
      fullLabel: user.user,
      user: user.user,
      value: commitData.commits,
      linesAdded: commitData.linesAdded,
      linesDeleted: commitData.linesDeleted,
      filesChanged: commitData.filesChanged,
      color: getUserColor(user.user, index),
      percentage: user.percentage,
    };
  });

  return {
    chartType: 'bar' as const,
    data: chartData,
    unit: 'commits',
  };
}

/**
 * Generate message-specific chart data
 */
function generateMessageChartData(data: StreamRecord[], topUsers: { user: string; count: number; percentage: number }[]) {
  const userMessageData = new Map<string, { messages: number; totalLength: number; avgLength: number }>();
  
  data.forEach(record => {
    const user = record.user_email || record.author_email || 'unknown';
    const existing = userMessageData.get(user) || { messages: 0, totalLength: 0, avgLength: 0 };
    const content = record.content || record.message || record.text || '';
    const length = typeof content === 'string' ? content.length : 0;
    
    existing.messages++;
    existing.totalLength += length;
    existing.avgLength = existing.totalLength / existing.messages;
    
    userMessageData.set(user, existing);
  });

  const chartData = topUsers.map((user, index) => {
    const messageData = userMessageData.get(user.user) || { messages: 0, totalLength: 0, avgLength: 0 };
    return {
      label: user.user.length > 15 ? user.user.substring(0, 12) + '...' : user.user,
      fullLabel: user.user,
      user: user.user,
      value: messageData.messages,
      avgLength: Math.round(messageData.avgLength),
      totalLength: messageData.totalLength,
      color: getUserColor(user.user, index),
      percentage: user.percentage,
    };
  });

  return {
    chartType: 'bar' as const,
    data: chartData,
    unit: 'messages',
  };
}

/**
 * Generate completion-specific chart data
 */
function generateCompletionChartData(data: StreamRecord[], topUsers: { user: string; count: number; percentage: number }[]) {
  const userCompletionData = new Map<string, { completions: number; totalTokens: number; avgTokens: number }>();
  
  data.forEach(record => {
    const user = record.user_email || record.author_email || 'unknown';
    const existing = userCompletionData.get(user) || { completions: 0, totalTokens: 0, avgTokens: 0 };
    const tokens = record.tokens || record.total_tokens || 0;
    
    existing.completions++;
    existing.totalTokens += tokens;
    existing.avgTokens = existing.totalTokens / existing.completions;
    
    userCompletionData.set(user, existing);
  });

  const chartData = topUsers.map((user, index) => {
    const completionData = userCompletionData.get(user.user) || { completions: 0, totalTokens: 0, avgTokens: 0 };
    return {
      label: user.user.length > 15 ? user.user.substring(0, 12) + '...' : user.user,
      fullLabel: user.user,
      user: user.user,
      value: completionData.completions,
      avgTokens: Math.round(completionData.avgTokens),
      totalTokens: completionData.totalTokens,
      color: getUserColor(user.user, index),
      percentage: user.percentage,
    };
  });

  return {
    chartType: 'bar' as const,
    data: chartData,
    unit: 'completions',
  };
}

/**
 * Statistics card component
 */
function StatCard({
  icon: Icon,
  label,
  value,
  subtitle,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  subtitle?: string;
  color: 'blue' | 'green' | 'purple' | 'orange';
}) {
  const colorClasses = {
    blue: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300',
    green: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
    purple: 'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300',
    orange: 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300',
  };

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <div className={`p-2 rounded-md ${colorClasses[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div>
        <p className="text-2xl font-bold" title={value}>
          {value.length > 20 ? value.substring(0, 17) + '...' : value}
        </p>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
